import ToastService from './ToastService';
import { APP_VERSION } from '../version';

const CHECK_INTERVAL_MS = 5 * 60 * 60 * 1000; // 5 hours
const LAST_CHECK_KEY = 'nordia_last_version_check';

class VersionService {
  constructor() {
    this.isChecking = false;
    this.isReloading = false;
    this.memoryLastCheckTime = 0;
    this.updateAvailable = false;
    this.serverVersion = null;
    this.listeners = new Set();

    this.cleanupOldCaches();
    this.setupListeners();

    // Check immediately on startup/open to ensure latest version is fetched
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        this.checkForUpdates();
      }, 1000);
    }
  }

  isUpdateAvailable() {
    return this.updateAvailable;
  }

  getServerVersion() {
    return this.serverVersion;
  }

  onUpdateAvailable(listener) {
    if (typeof listener === 'function') {
      this.listeners.add(listener);
      if (this.updateAvailable) {
        listener({
          serverVersion: this.serverVersion,
          currentVersion: APP_VERSION,
        });
      }
      return () => {
        this.listeners.delete(listener);
      };
    }
    return () => {};
  }

  notifyUpdateAvailable(serverVersion) {
    if (serverVersion && serverVersion > APP_VERSION) {
      const isNew = !this.updateAvailable || this.serverVersion !== serverVersion;
      this.updateAvailable = true;
      this.serverVersion = serverVersion;

      if (isNew) {
        console.log(`[VersionService] Newer version available: ${serverVersion} (current: ${APP_VERSION})`);
        this.listeners.forEach((listener) => {
          try {
            listener({
              serverVersion: this.serverVersion,
              currentVersion: APP_VERSION,
            });
          } catch (e) {
            console.error('[VersionService] Error in update listener:', e);
          }
        });

        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('nordia_version_update_available', {
              detail: { serverVersion, currentVersion: APP_VERSION },
            })
          );
        }
      }
    }
  }

  getLastCheckTime() {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        const stored = localStorage.getItem(LAST_CHECK_KEY);
        if (stored) {
          const parsed = parseInt(stored, 10);
          if (!isNaN(parsed) && parsed > 0) {
            return parsed;
          }
        }
      } catch (e) {
        // Fallback to memory
      }
    }
    return this.memoryLastCheckTime;
  }

  setLastCheckTime(time) {
    this.memoryLastCheckTime = time;
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(LAST_CHECK_KEY, time.toString());
      } catch (e) {
        // Ignore storage errors
      }
    }
  }

  resetLastCheckTime() {
    this.memoryLastCheckTime = 0;
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(LAST_CHECK_KEY);
      } catch (e) {
        // Ignore storage errors
      }
    }
  }

  cleanupOldCaches() {
    if (typeof window === 'undefined') return Promise.resolve();

    const swPromise = ('serviceWorker' in navigator)
      ? navigator.serviceWorker.getRegistrations().then((registrations) => {
          return Promise.all(registrations.map((reg) => reg.unregister().catch(() => {})));
        }).catch((err) => {
          console.warn('[VersionService] Failed to unregister service workers:', err);
        })
      : Promise.resolve();

    const cachePromise = ('caches' in window)
      ? caches.keys().then((keys) => {
          return Promise.all(keys.map((key) => caches.delete(key).catch(() => {})));
        }).catch((err) => {
          console.warn('[VersionService] Failed to clear caches:', err);
        })
      : Promise.resolve();

    return Promise.all([swPromise, cachePromise]);
  }

  setupListeners() {
    if (typeof window === 'undefined') return;

    // Check for updates when coming back online
    window.addEventListener('online', () => {
      console.log('[VersionService] Device online, checking for newer version...');
      this.checkForUpdates();
    });

    // Check for updates when window gains focus or becomes visible
    window.addEventListener('focus', () => {
      this.checkForUpdates();
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkForUpdates();
        }
      });
    }

    // Periodic check every 60 seconds
    setInterval(() => {
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        this.checkForUpdates();
      }
    }, 60 * 1000);
  }

  getVersionInfoUrl() {
    return `/version.info?_t=${Date.now()}`;
  }

  async checkForUpdates(force = false) {
    if (this.isChecking || typeof window === 'undefined' || typeof fetch === 'undefined') {
      return false;
    }

    const now = Date.now();
    if (!force && (now - this.getLastCheckTime() < CHECK_INTERVAL_MS)) {
      return false;
    }

    this.isChecking = true;
    try {
      const verRes = await fetch(this.getVersionInfoUrl(), {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      if (verRes.ok) {
        const text = (await verRes.text()).trim();
        if (/^\d+$/.test(text)) {
          const serverVersion = parseInt(text, 10);
          if (serverVersion > APP_VERSION) {
            console.log(`[VersionService] Newer version detected via version.info (${serverVersion} > ${APP_VERSION})`);
            this.notifyUpdateAvailable(serverVersion);
            return true;
          }
        }
      }
      this.setLastCheckTime(now);
    } catch (e) {
      console.warn('[VersionService] Update check failed:', e);
      this.setLastCheckTime(now);
    } finally {
      this.isChecking = false;
    }

    return false;
  }

  async forceReloadWithoutCache() {
    if (this.isReloading) return;
    this.isReloading = true;
    console.log('[VersionService] Force reload without cache requested.');

    try {
      await this.cleanupOldCaches();
    } catch (e) {
      console.warn('[VersionService] Cache cleanup error before reload:', e);
    }

    this.reloadApp();
  }

  // Alias for backward compatibility
  async handleForceReload() {
    await this.forceReloadWithoutCache();
  }

  reloadApp() {
    if (typeof window !== 'undefined' && window.location) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('_v', Date.now().toString());
        window.location.replace(url.toString());
      } catch (e) {
        window.location.reload();
      }
    }
  }
}

const versionServiceInstance = new VersionService();
export default versionServiceInstance;
export { VersionService };
